import { HOME } from "@/constants/testIds";
import { MapPin, Clock, Phone, Mail, Facebook, Instagram, Linkedin, MessageCircle } from "lucide-react";

const Footer = ({ settings = {} }) => {
  const s = settings;
  const social = s.social || {};
  const waHref = s.whatsapp_number ? `https://wa.me/${String(s.whatsapp_number).replace(/[^\d]/g, "")}` : "#";

  return (
    <footer
      data-testid={HOME.footer}
      className="bg-[#1A1412] text-[#c4beb8] pt-16 pb-8"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
          {/* Logo & Message */}
          <div className="md:col-span-2">
            {s.logo_image_footer || s.logo_image ? (
              <img
                src={s.logo_image_footer || s.logo_image}
                alt={s.brand_name || "Rudralife"}
                className="h-14 md:h-20 w-auto object-contain"
              />
            ) : (
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-[#FAF6F0] font-serif text-xl" style={{ background: "radial-gradient(circle at 30% 30%, #b46a2a, #6b2f0a 70%, #2a1005)" }}>ॐ</div>
                <div>
                  <div className="font-serif text-2xl text-white leading-none">{s.brand_name || "Rudralife"}</div>
                  <div className="text-[10px] tracking-[0.3em] uppercase text-[#D4AF37] mt-1">{s.tagline || "Rudraksha power for you"}</div>
                </div>
              </div>
            )}
            <p className="mt-6 text-sm leading-relaxed max-w-md">
              For 25 years, Rudralife has remained the trusted home of authentic Nepali Rudraksha. Every bead we deliver carries the blessing of three generations and the assurance of lab-certified authenticity — because your devotion deserves nothing less.
            </p>

            {/* Social */}
            <div className="mt-6 flex gap-3">
              {social.facebook && (
                <a href={social.facebook} target="_blank" rel="noreferrer" data-cursor="button" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#1A1412] hover:border-[#D4AF37] transition-colors">
                  <Facebook size={16} />
                </a>
              )}
              {social.instagram && (
                <a href={social.instagram} target="_blank" rel="noreferrer" data-cursor="button" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#1A1412] hover:border-[#D4AF37] transition-colors">
                  <Instagram size={16} />
                </a>
              )}
              {(social.linkedin || social.youtube) && (
                <a href={social.linkedin || social.youtube} target="_blank" rel="noreferrer" data-cursor="button" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#1A1412] hover:border-[#D4AF37] transition-colors">
                  <Linkedin size={16} />
                </a>
              )}
              <a href={waHref} target="_blank" rel="noreferrer" data-cursor="button" className="w-10 h-10 rounded-full border border-white/15 flex items-center justify-center hover:bg-[#25D366] hover:text-white hover:border-[#25D366] transition-colors">
                <MessageCircle size={16} />
              </a>
            </div>
          </div>

          {/* Visit */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Visit Us</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex items-start gap-2">
                <MapPin size={15} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <span>{s.address}</span>
              </li>
              <li className="flex items-start gap-2">
                <Clock size={15} className="text-[#D4AF37] mt-1 flex-shrink-0" />
                <div>
                  <div>{s.open_days}</div>
                  <div className="text-[#D4AF37]">{s.open_time}</div>
                </div>
              </li>
              <li className="flex items-center gap-2">
                <Phone size={15} className="text-[#D4AF37]" />
                <a data-cursor="button" href={`tel:${s.phone}`} className="hover:text-white">{s.phone}</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail size={15} className="text-[#D4AF37]" />
                <a data-cursor="button" href={`mailto:${s.email}`} className="hover:text-white">{s.email}</a>
              </li>
            </ul>
          </div>

          {/* Explore Links */}
          <div>
            <h4 className="font-serif text-lg text-white mb-4">Explore</h4>
            <ul className="space-y-2 text-sm">
              {[
                ["Siddha Mala", "#siddha-mala"],
                ["Shravan Pujas", "#shravan-pujas"],
                ["Live Exhibitions", "#exhibition-hero"],
                ["Combo Kits", "#combo-kits"],
                ["Rare Collection", "#rare-collection"],
                ["Rudraksha Recommendation", "https://rudralife.com/rudraksha-recommendation"],
              ].map(([label, href]) => (
                <li key={href}>
                  <a data-cursor="button" href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className="hover:text-[#D4AF37] transition-colors">
                    {label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 mt-12 pt-6 flex flex-col md:flex-row items-center justify-between text-xs text-[#807a74]">
          <div>© {new Date().getFullYear()} {s.brand_name || "Rudralife"}. Silver Jubilee Edition · All rights reserved.</div>
          <div className="mt-3 md:mt-0 tracking-[0.2em] uppercase">ॐ नमः शिवाय</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
